import { get, URLX, getClientApiUrl } from '@avensia/excite';
import { useEffect, useMemo, useState } from 'react';
import * as searchHistory from './search-history';

export const SEARCH_SORT_NAME = 'sortBy';
export const SEARCH_QUERY_NAME = 'q';
export const SEARCH__NOTIFY_QUERY_NAME = 'notify';
export const SEARCH_USED_SUGGESTION = 'suggested';
export const SEARCH_USED_HISTORY = 'history';
export const SEARCH_ORIGINAL_QUERY = 'original';
export const SEARCH_PAGE_REFERRER = 'referrer';

let lastRequestId = 0;

export function useSearchSuggestions(searchText: string, historyCount = 3, fetchSuggestions = true) {
  const filteredSearchHistory = useMemo(
    () =>
      searchHistory
        .getAll(searchTextToWords(searchText))
        .filter(phrase => phrase.toLowerCase() !== searchText.toLowerCase())
        .slice(0, historyCount)
        .map(phrase => ({ text: phrase } as NordicNest.Api.SearchSuggestionApiModel)),
    [searchText, historyCount],
  );

  const [autoCompeleteSuggestions, setAutoCompeleteSuggestions] = useState<NordicNest.Api.SearchSuggestionApiModel[]>(
    [],
  );
  const [searchSuggestions, setSearchSuggestions] = useState<NordicNest.Api.SearchSuggestionApiModel[]>([]);
  const [historySuggestions, setHistorySuggestions] = useState<NordicNest.Api.SearchSuggestionApiModel[]>([]);

  useEffect(() => {
    setHistorySuggestions(filteredSearchHistory);
    if (fetchSuggestions) {
      const requestId = (lastRequestId = Math.random());
      if (searchText.length > 0) {
        getAutoCompleteSuggestions(searchText, requestId).then(suggestions => {
          setAutoCompeleteSuggestions(suggestions);
        });
      } else {
        getSearchSuggestions(searchText, requestId).then(suggestions => {
          setSearchSuggestions(suggestions);
        });
      }
    }
  }, [searchText, filteredSearchHistory, fetchSuggestions]);

  return { autoCompeleteSuggestions, searchSuggestions, historySuggestions };
}

function getSearchSuggestions(searchText: string, requestId: number) {
  const url = getClientApiUrl('searchsuggestions');
  url.searchParams.set(SEARCH_QUERY_NAME, searchText);

  return get(url)
    .then(r => r.json())
    .then((json: NordicNest.Api.SearchSuggestionApiModel[]) => {
      if (requestId === lastRequestId) {
        return json;
      } else {
        return [];
      }
    })
    .catch(() => {
      return [];
    });
}

function getAutoCompleteSuggestions(searchText: string, requestId: number) {
  const url = getClientApiUrl('autocomplete');
  url.searchParams.set(SEARCH_QUERY_NAME, searchText);

  return get(url)
    .then(r => r.json())
    .then((json: NordicNest.Api.SearchSuggestionApiModel[]) => {
      if (requestId === lastRequestId) {
        return json;
      } else {
        return [];
      }
    })
    .catch(() => {
      return [];
    });
}

export function searchTextToWords(searchText: string) {
  return searchText.replace(/\s+/, ' ').split(' ');
}

export function searchUrl(
  searchPageUrl: string,
  query: string,
  notify: boolean,
  usedSuggestion: boolean,
  usedHistory: boolean,
  originalQuery: string,
  pageReferrer: string,
) {
  const url = new URLX(searchPageUrl);
  url.searchParams.set(SEARCH_QUERY_NAME, query.trim());
  url.hiddenParams.set(SEARCH__NOTIFY_QUERY_NAME, String(notify));
  url.hiddenParams.set(SEARCH_USED_SUGGESTION, String(usedSuggestion));
  url.hiddenParams.set(SEARCH_USED_HISTORY, String(usedHistory));
  url.hiddenParams.set(SEARCH_ORIGINAL_QUERY, originalQuery.trim());
  url.hiddenParams.set(SEARCH_PAGE_REFERRER, pageReferrer.trim());

  return url;
}
